import { Tooltip } from "@mui/material";
import { NeutralColor } from "node-library/dist/colors";
import { evaluateFrontendCalculationFormula } from "node-library/dist/helpers/workflow-formula-evaluation-helper";
import {
	FrontendCompanyTestResultUnits,
	FrontendCompanyWithAdditionalInfomation,
} from "node-library/dist/portal";
import { FrontendTestResult } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import {
	FrontendCompanyResultCalculationFormula,
	FrontendCompanyWorkflowResultFields,
} from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlackButton } from "../../../../../common-components/button";
import {
	CustomTextInput,
	TableDropdown,
} from "../../../../../common-components/inputs";
import { YellowWarnIcon } from "../../../../../common-components/toast/warnIcons";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	resultField: FrontendCompanyWorkflowResultFields;
	relatedValues: Record<string, string>; //infoId, value
	instanceId: string;
	currentIteration: number;
	recordedResult: FrontendTestResult | undefined;
	hasDeviation: boolean;
	setRecordedResult: (result: FrontendTestResult) => void;
	setResultDeviationModalOpen: (open: boolean) => void;
}

export function WorkflowResultField(props: Props) {
	const { t: translation } = useTranslation();

	const {
		company,
		resultField,
		relatedValues,
		instanceId,
		currentIteration,
		recordedResult,
		hasDeviation,
		setRecordedResult,
		setResultDeviationModalOpen,
	} = props;

	const formula: FrontendCompanyResultCalculationFormula | undefined =
		resultField.resultCalculationFormula;

	const availableUnits: Array<FrontendCompanyTestResultUnits> =
		company.testResultUnits.filter((unit) => {
			return resultField.availableUnitIds.includes(unit.unitId);
		});

	const [resultUnitId, setResultUnitId] = useState<string>(
		recordedResult
			? recordedResult.resultUnitId
			: resultField.availableUnitIds[0],
	);

	useEffect(() => {
		if (formula && (!recordedResult || !hasDeviation)) {
			const result: string | number | boolean | undefined =
				evaluateFrontendCalculationFormula(formula, relatedValues);
			let frontendResult: FrontendTestResult;
			if (!recordedResult) {
				frontendResult = {
					sampleInstanceId: instanceId,
					entryId: "",
					resultPackageId: "",
					resultFieldId: resultField.resultFieldId,
					value: result ? String(result) : undefined,
					resultUnitId: resultUnitId,
					iteration: currentIteration,
					hasDeviation: false,
				};
			} else {
				frontendResult = {
					...recordedResult,
					value: result ? String(result) : undefined,
				};
			}

			setRecordedResult(frontendResult);
		}
	}, [relatedValues, hasDeviation]);

	const resultFieldComponent: JSX.Element = (
		<CustomTextInput
			id={`${instanceId}-${resultField.resultFieldId}`}
			key={`${instanceId}-${resultField.resultFieldId}`}
			value={recordedResult?.value || ""}
			setValue={() => {}}
			// placeholder={translation("Confirm")}
			type="text"
			required={true}
			disabled
		></CustomTextInput>
	);

	const unitDropdown: JSX.Element = (
		<TableDropdown
			id={`${instanceId}-${resultField.resultFieldId}-dropdown`}
			label={""}
			options={availableUnits.map((unit: FrontendCompanyTestResultUnits) => {
				return {
					title: unit.unit,
					value: unit.unitId,
				};
			})}
			value={resultUnitId}
			setValue={setResultUnitId}
		></TableDropdown>
	);
	const deviationButton = (
		<BlackButton
			key={`${instanceId}-${resultField.resultFieldId}-deviation-button`}
			id={`${instanceId}-${resultField.resultFieldId}-deviation-button`}
			children={translation("Deviation")}
			onClick={() => {
				setResultDeviationModalOpen(true);
			}}
		></BlackButton>
	);

	return (
		<div style={{ display: "flex", gap: "5px" }}>
			{hasDeviation && (
				<div style={{ display: "flex", alignItems: "center" }}>
					<Tooltip
						PopperProps={{
							sx: {
								"& .MuiTooltip-tooltip": {
									backgroundColor: NeutralColor.NEUTRAL_GREY_950,
									padding: "12px 16px",
								},
							},
						}}
						title={`${translation("ResultDeviatesFromFormulaDescription")}`}
						arrow
					>
						<span style={{ display: "flex", width: "fit-content" }}>
							<YellowWarnIcon></YellowWarnIcon>
						</span>
					</Tooltip>
				</div>
			)}
			{resultFieldComponent}
			{unitDropdown}
			{deviationButton}
		</div>
	);
}
